"use client";

import { memo, useMemo } from "react";
import { ArrowRight, Check, Pencil } from "lucide-react";
import type { FormField } from "@/lib/database/forms";
import type { SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";
import { formatValue } from "@/features/forms/_logic/field-utils";
import { HtmlLabel } from "./HtmlLabel";

/** Field types formatValue can't safely render for a public respondent (object/path values). */
const NON_RECAP_FIELD_TYPES: ReadonlySet<string> = new Set(["section", "description", "signature", "file", "address"]);

/** Shared CTA classes/style, matching FormThankYou's compact-state footprint. */
const EDIT_BUTTON_CLASS =
  "flex w-full max-w-sm touch-manipulation items-center justify-center gap-2 whitespace-nowrap rounded-lg px-6 py-3 text-sm font-semibold transition-all [@media(hover:hover)]:hover:opacity-90";

/** Outlined variant, used for Edit when "Continue to next step" is the primary action. */
const SECONDARY_BUTTON_CLASS =
  "flex w-full max-w-sm touch-manipulation items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-all [@media(hover:hover)]:hover:bg-gray-50";

interface FormAlreadySubmittedProps {
  /** Form name, shown as an eyebrow above the heading (matches FormThankYou). */
  formName: string | null;
  /** The viewer's existing response, bundled into the same load as the form schema. */
  recap: SubmittedFormResponse;
  /** Field definitions, used to label the answer recap in form order. */
  formFields: FormField[];
  /** Whether the current viewer is authenticated and may edit this submission. */
  canEdit: boolean;
  onEdit: () => void;
  /**
   * Set when this form is a step of a multi-step CTA that can move on from this earlier submission.
   * Shows "Continue to next step" as the primary action, and Edit becomes the secondary one.
   */
  onContinue?: () => void;
  /**
   * Set when the guest may add another response (a multi-step CTA step on a form that accepts
   * several per person). Shown as a quiet link, since editing is usually what they want.
   */
  onSubmitAgain?: () => void;
  /** Form theme color for the edit button. */
  primaryColor: string;
  /** Readable text color over `primaryColor`. */
  buttonTextColor: string;
}

/**
 * Shown in place of a blank form when the viewer already submitted a
 * single-submission form — echoes their identity and prior answers, with an
 * "Edit my submission" CTA for authenticated owners.
 *
 * @param props - Recap props
 * @returns Already-submitted recap element
 */
export const FormAlreadySubmitted = memo(function FormAlreadySubmitted({
  formName,
  recap,
  formFields,
  canEdit,
  onEdit,
  onContinue,
  onSubmitAgain,
  primaryColor,
  buttonTextColor,
}: FormAlreadySubmittedProps): JSX.Element {
  const answerEntries = useMemo(
    () =>
      formFields
        .filter((field) => !NON_RECAP_FIELD_TYPES.has(field.type))
        .map((field) => ({ label: field.label, value: formatValue(recap.answers[field.label], field) }))
        .filter((entry) => entry.value !== ""),
    [formFields, recap.answers]
  );

  const submittedDate = useMemo(
    () =>
      recap.submittedAt
        ? new Date(recap.submittedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
        : null,
    [recap.submittedAt]
  );

  const editButtonStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}DD 100%)`,
      color: buttonTextColor,
    }),
    [primaryColor, buttonTextColor]
  );

  return (
    <div className="flex flex-col items-center gap-6 pb-6 pt-1 text-center">
      <div className="flex flex-col items-center gap-3">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full"
          style={{ backgroundColor: `${primaryColor}1A` }}
        >
          <Check className="h-7 w-7" strokeWidth={2.5} style={{ color: primaryColor }} aria-hidden="true" />
        </div>
        <div className="flex flex-col items-center gap-2">
          {formName && <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{formName}</span>}
          <h3 className="text-2xl font-bold text-gray-900">You already submitted this form</h3>
          {recap.email && (
            <p className="text-sm font-medium text-gray-600">
              Submitted as {recap.email}
              {submittedDate ? ` on ${submittedDate}` : ""}
            </p>
          )}
        </div>
      </div>

      {answerEntries.length > 0 && (
        <div className="w-full max-w-sm rounded-2xl bg-gray-50 p-4 text-left">
          <h4
            id="already-submitted-answers-heading"
            className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            Your answers
          </h4>
          {/* Bounded + independently scrollable so a long answer list can't push the
              Edit button below the fold — only this list scrolls, not the whole modal. */}
          <dl aria-labelledby="already-submitted-answers-heading" className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {answerEntries.map((entry) => (
              <div key={entry.label} className="flex flex-col gap-0.5 text-sm">
                <dt className="min-w-0 wrap-break-word font-medium text-gray-500">
                  <HtmlLabel html={entry.label} />
                </dt>
                <dd className="min-w-0 wrap-break-word font-medium text-gray-900">{entry.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {onContinue && (
        <button type="button" onClick={onContinue} className={EDIT_BUTTON_CLASS} style={editButtonStyle}>
          Continue to next step
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {canEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className={onContinue ? SECONDARY_BUTTON_CLASS : EDIT_BUTTON_CLASS}
          style={onContinue ? undefined : editButtonStyle}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Edit my submission
        </button>
      ) : (
        <p className="text-sm font-medium text-gray-500">Need to change something? Reach out to the organizer.</p>
      )}

      {onSubmitAgain && (
        <button
          type="button"
          onClick={onSubmitAgain}
          className="touch-manipulation rounded px-2 py-3 text-sm font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          Submit again
        </button>
      )}
    </div>
  );
});

FormAlreadySubmitted.displayName = "FormAlreadySubmitted";
